import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  MdApps,
  MdHelpOutline,
  MdOutlineAccountTree,
  MdOutlineHome,
  MdOutlineMenuBook,
  MdOutlinePsychology,
  MdOutlineSettings,
} from 'react-icons/md';
import { Tooltip } from '@aibuild/ui';
import NavigationDrawer from '@components/2-molecules/NavigationDrawer';
import ConversationList from '@components/ConversationList';
import ProductSwitcher from '@components/ProductSwitcher';
import { JIRA_SUPPORT_DESK_URL } from '@constants/index';
import { RAIL_TOOLTIP } from '@components/SidebarRail';
import { useSidebar } from '@contexts/useSidebar';
import { FeatureGate, useFeatureGate } from '@featureFlags/index';
import { WorkspaceLabel, WorkspaceSlot } from './NavigationBar.styled';

/**
 * The app menu. A caller that owns its own rail (the Process editor) passes
 * `onCollapse`; the menu then never folds into the shared rail itself.
 */
const NavigationBar = ({ onCollapse }) => {
  const intl = useIntl();
  const isProcessesEnabled = useFeatureGate(FeatureGate.OS_PROCESSES_PAGE);
  const isAppsEnabled = useFeatureGate(FeatureGate.OS_APPS);
  const { isNavCollapsed, setNavCollapsed, openProductSwitcher } = useSidebar();
  const collapsed = !onCollapse && isNavCollapsed;

  const collapse = useCallback(() => setNavCollapsed(true), [setNavCollapsed]);
  const expand = useCallback(() => setNavCollapsed(false), [setNavCollapsed]);
  const switchWorkspace = useCallback(() => {
    openProductSwitcher();
    setNavCollapsed(false);
  }, [openProductSwitcher, setNavCollapsed]);

  const navigationItems = useMemo(() => {
    const items = [
      {
        id: 'home',
        iconComponent: MdOutlineHome,
        label: intl.formatMessage({ id: 'nav.home', defaultMessage: 'Home' }),
        to: '/',
        exact: true,
      },
    ];

    if (isAppsEnabled) {
      items.push({
        id: 'apps',
        iconComponent: MdApps,
        label: intl.formatMessage({ id: 'nav.apps', defaultMessage: 'Apps' }),
        to: '/apps',
        exact: true,
      });
    }

    if (isProcessesEnabled) {
      items.push({
        id: 'processes',
        iconComponent: MdOutlineAccountTree,
        label: intl.formatMessage({ id: 'nav.processes', defaultMessage: 'Processes' }),
        to: '/processes',
      });
    }

    items.push(
      {
        id: 'instructions',
        iconComponent: MdOutlineMenuBook,
        label: intl.formatMessage({ id: 'nav.instructions', defaultMessage: 'Instructions' }),
        to: '/instructions',
      },
      {
        id: 'skills',
        iconComponent: MdOutlinePsychology,
        label: intl.formatMessage({ id: 'nav.skills', defaultMessage: 'Skills' }),
        to: '/skills',
      },
      {
        id: 'settings',
        iconComponent: MdOutlineSettings,
        label: intl.formatMessage({ id: 'nav.settings', defaultMessage: 'Settings' }),
        to: '/settings',
      },
      {
        id: 'help-support',
        iconComponent: MdHelpOutline,
        label: intl.formatMessage({ id: 'nav.helpSupport', defaultMessage: 'Help & Support' }),
        to: JIRA_SUPPORT_DESK_URL,
        external: true,
      },
    );

    return items;
  }, [intl, isAppsEnabled, isProcessesEnabled]);

  const switchWorkspaceLabel = intl.formatMessage({
    id: 'nav.sidebar.switchWorkspace',
    defaultMessage: 'Switch workspace',
  });

  const workspace = collapsed ? (
    <WorkspaceSlot>
      <Tooltip content={switchWorkspaceLabel} {...RAIL_TOOLTIP}>
        <WorkspaceLabel
          type="button"
          onClick={switchWorkspace}
          aria-label={switchWorkspaceLabel}
          data-testid="navigation-bar__workspace"
        >
          OS
        </WorkspaceLabel>
      </Tooltip>
    </WorkspaceSlot>
  ) : (
    <ProductSwitcher />
  );

  return (
    <NavigationDrawer
      items={navigationItems}
      withLogo
      collapsed={collapsed}
      onCollapse={onCollapse ?? collapse}
      onExpand={expand}
      subHeader={workspace}
      extraContent={<ConversationList />}
    />
  );
};

NavigationBar.propTypes = {
  onCollapse: PropTypes.func,
};

export default NavigationBar;
